/**
 * Lo que comparten las piezas del asistente de campaña del oficial (`pages/CampanaAsistentePage.tsx`,
 * `hooks/asistente/` y `components/asistente/`). Puro.
 */

import type { AudienciaActiva, CampanaDetalle, CampanaResumen, MapeoVariables, ModoTransporte, ResultadoPrueba } from "../types";
import { PASOS, type PasoAsistente } from "./chequeo";
import type { VueltaDelEditor } from "./editorCampana";
import { unirConY } from "./exclusiones";
import { horarioEnEdicion, mismoHorario } from "./horario";
import { eleccionDeBaja } from "./baja";
import { mismaAudienciaGuardada } from "./tuCampana";
import { audienciaEnPalabras } from "./vasAMandar";
import { motivoPlantillaNoUsable } from "./textos";
import { fecha, num, plural } from "../../campana-nueva/utils/format";
import { ZONA_MARCA, isoALocalEnZona } from "./zonas";

export const idx = (p: PasoAsistente) => PASOS.indexOf(p);
export const esPaso = (s: string | null): s is PasoAsistente => !!s && (PASOS as string[]).includes(s);

/**
 * Los seis pasos de antes (hasta el 29/10/2026) siguen llegando por nombre en links viejos: las alertas, un seguimiento,
 * «Armar en el oficial», las listas de exclusión, la vuelta del editor o de la Base, un marcador. Cada uno lleva al paso
 * nuevo donde quedó lo suyo: la plantilla y las variables, a «Qué les decís»; el horario, a «Cuándo»; la prueba y el
 * confirmar, a «Revisar y mandar».
 */
export const ALIAS_PASOS: ReadonlyMap<string, PasoAsistente> = new Map([
  ["plantilla", "mensaje"], ["variables", "mensaje"], ["horario", "cuando"], ["prueba", "revisar"], ["confirmar", "revisar"],
]);
/** El paso que pide `?paso=`, con los nombres viejos ya traducidos (`ALIAS_PASOS`); null si no es ninguno. */
export const pasoDeLaUrl = (s: string | null): PasoAsistente | null => (esPaso(s) ? s : (s && ALIAS_PASOS.get(s)) || null);

/** Los tres tipos de audiencia guardados (y sus pestañas del paso Audiencia). */
export type TipoPestana = "base" | "activa" | "publico";
/**
 * Las pestañas del paso Audiencia: las tres de los tipos y «Subir un Excel», que no guarda nada (lleva a la carga de la
 * Base y vuelve con el público del archivo, en la pestaña de la base).
 */
export type Pestana = TipoPestana | "excel";

/** Un aviso con un link opcional (errores de la prueba y del confirmar). */
export type Aviso = { texto: string; link?: { label: string; ruta: string } };

/**
 * El botón de un paso, en la barra de abajo: «Siguiente» o lo que diga `label`. `motivo` es por qué todavía no se puede
 * seguir, o null si se puede: va debajo del botón apagado, y es "" cuando lo que falta ya está a la vista (los
 * bloqueos del chequeo, en Confirmar). `guardando`: el paso está guardando y el botón no se toca.
 * Cada paso arma el suyo (el `cta` de cada hook de `hooks/asistente/`).
 */
export type CtaPaso = { motivo: string | null; onSiguiente: () => void | Promise<void>; label?: string; guardando?: boolean };

/**
 * Lo que un paso tiene en pantalla y todavía no guardó, para saltar a otro sin «Siguiente» (el stepper, «Tu campaña»,
 * «Atrás»): antes de irse se pregunta en la misma pantalla (`PreguntaSinGuardar`). `noSeGuarda` es por qué todavía no
 * se puede guardar (null si se puede). `guardar` y `descartar` terminan en `destino` o, si lo guardado ya no deja
 * llegar, en el paso más lejano que sí (`hastaDonde`).
 */
export type CambiosDelPaso = {
  sinGuardar: boolean;
  noSeGuarda: string | null;
  guardar: (destino: PasoAsistente) => Promise<void>;
  descartar: (destino: PasoAsistente) => void;
};

/** Lo que no son cifras y otra pestaña puede cambiar: con cualquiera de esto, «Revisar y mandar» lo dice antes de mandar. */
export type CambioGuardado = "plantilla" | "mapeo" | "audiencia" | "horario" | "baja";
const NOMBRE_DEL_CAMBIO: Record<CambioGuardado, string> = {
  plantilla: "la plantilla", mapeo: "las variables", audiencia: "a quién", horario: "el horario", baja: "la baja",
};

/** El mapeo en un texto para comparar: las claves en orden (la base puede devolverlas en otro). */
function claveMapeo(m: MapeoVariables | null | undefined): string {
  const ordenado = (x: unknown): unknown => (x && typeof x === "object" && !Array.isArray(x)
    ? Object.fromEntries(Object.keys(x).sort().map((k) => [k, ordenado((x as Record<string, unknown>)[k])]))
    : x);
  return JSON.stringify(ordenado(m || {}));
}

/**
 * Qué cambió de lo guardado entre `antes` (lo que la pantalla tiene) y `ahora` (lo que trae `GET /campanas/:id`): la
 * plantilla, las variables, a quién, el horario (y si ya se eligió) o la baja. Las cifras (cuántos, el saldo) no: ésas
 * las trae el chequeo.
 */
export function queCambio(
  antes: Pick<CampanaDetalle, "plantilla" | "mapeo" | "audiencia" | "horario" | "horarioElegido" | "baja">,
  ahora: Pick<CampanaDetalle, "plantilla" | "mapeo" | "audiencia" | "horario" | "horarioElegido" | "baja">,
): CambioGuardado[] {
  const out: CambioGuardado[] = [];
  if ((antes.plantilla?.id || "") !== (ahora.plantilla?.id || "")) out.push("plantilla");
  if (claveMapeo(antes.mapeo) !== claveMapeo(ahora.mapeo)) out.push("mapeo");
  if (!mismaAudienciaGuardada(antes.audiencia, ahora.audiencia)) out.push("audiencia");
  if (!mismoHorario(horarioEnEdicion(antes.horario), horarioEnEdicion(ahora.horario)) || (antes.horarioElegido !== false) !== (ahora.horarioElegido !== false)) out.push("horario");
  if (eleccionDeBaja(antes.baja) !== eleccionDeBaja(ahora.baja)) out.push("baja");
  return out;
}

/**
 * «Esta campaña cambió en otra pestaña (la plantilla y a quién): ahora es «Promo» a «Todos tus clientes · todos los
 * segmentos». Revisala antes de mandar.»: lo que cambió y cómo quedó, con lo que la campaña tiene guardado ahora.
 */
export function textoCambioEnOtraPestana(cambios: CambioGuardado[], c: Pick<CampanaDetalle, "plantilla" | "audiencia" | "seguimientoDe">, audiencias: AudienciaActiva[] | null | undefined): string {
  const nombres = cambios.map((x) => NOMBRE_DEL_CAMBIO[x]);
  const que = nombres.length > 1 ? `${nombres.slice(0, -1).join(", ")} y ${nombres[nombres.length - 1]}` : nombres[0] ?? "";
  const grupos = (audiencias || []).find((a) => a.id === c.audiencia?.campanaActivaId)?.grupos;
  const plantilla = c.plantilla ? `«${c.plantilla.nombreVisible}»` : "sin plantilla";
  return `Esta campaña cambió en otra pestaña (${que}): ahora es ${plantilla} a «${audienciaEnPalabras(c, { grupos }).origen}». Revisala antes de mandar.`;
}

/**
 * Los rechazos del chequeo (y del confirmar) que no se arreglan reintentando sino en otro paso: un público, una
 * segmentación o una lista de exclusión que se borraron, o un grupo que ya no está, en «A quién»; una variable sin su
 * dato o una plantilla que ya no se puede usar, en «Qué les decís». null si no es uno de ésos.
 */
export function pasoQueArregla(code: string | null | undefined): PasoAsistente | null {
  if (code === "AUDIENCIA_INVALIDA" || code === "GRUPO_DESCONOCIDO") return "audiencia";
  if (code === "MAPEO_INVALIDO" || code === "PLANTILLA_NO_APROBADA" || code === "PLANTILLA_NO_ASOCIABLE" || code === "PLANTILLA_SIMULADA" || code === "PLANTILLA_DE_OTRA_CUENTA") return "mensaje";
  return null;
}

/** Adónde se llega yendo a `destino` con lo guardado: él mismo si se alcanza (`max`, un índice de `PASOS`), si no el más lejano que sí. */
export function hastaDonde(destino: PasoAsistente, max: number): PasoAsistente {
  return idx(destino) <= max ? destino : PASOS[Math.max(0, Math.min(max, PASOS.length - 1))];
}

/**
 * Una plantilla que no sirve con la cuenta conectada (simulada en modo Twilio, o de otra cuenta). La
 * regla es la de `motivoPlantillaNoUsable`, la MISMA que el backend: acá sólo se pregunta sí o no.
 */
export const esInutilizable = (p: Parameters<typeof motivoPlantillaNoUsable>[0], modo: ModoTransporte | null) => !!motivoPlantillaNoUsable(p, modo);

/**
 * Lo que deja la página del editor (`campanas/:id/plantilla`) al volver: un aviso («Mandada a Meta…») o
 * un error (no se pudo mandar, no se pudo asociar, ya no existe). Llega en `location.state` y no en la
 * URL para que un F5 no lo repita. Se lee con cuidado: el state de una entrada vieja del historial
 * puede ser cualquier cosa.
 */
export function leerVuelta(s: unknown): { aviso: string; error: string } {
  const v = (s && typeof s === "object" ? s : {}) as { [K in keyof VueltaDelEditor]?: unknown };
  return {
    aviso: typeof v.avisoPlantilla === "string" ? v.avisoPlantilla : "",
    error: typeof v.errorPlantilla === "string" ? v.errorPlantilla : "",
  };
}

/**
 * Lo que el operador eligió en el asistente y todavía no guardó, para el viaje a la página del editor:
 * ir allá desmonta el asistente y, al volver, todo sale de la base (con la ventana de antes el asistente
 * seguía montado abajo y no se perdía nada). La plantilla elegida a mano y la prueba del paso 4 van acá;
 * el nombre no, se guarda en el servidor antes de irse, como el resto del borrador (`guardarNombre`).
 * En sessionStorage, por campaña: vale para esta pestaña, se lee UNA vez al volver y se borra. `asociada`
 * es la plantilla que la campaña tenía al irse: si al volver es otra, el editor asoció una nueva y manda
 * el servidor (lo elegido y la prueba eran de la anterior). La prueba va sin el texto: lleva datos de un
 * contacto de la audiencia, y al volver lo que hace falta es saber cómo salió.
 */
export type FotoAsistente = { plantillaId: string; asociada: string; sinPrueba: boolean; prueba: ResultadoPrueba | null; pruebaInicio: number };
const claveFoto = (idCampana: string) => `deenex-activa:oficial:asistente:${idCampana}`;
export function guardarFoto(idCampana: string, f: FotoAsistente): void {
  try { sessionStorage.setItem(claveFoto(idCampana), JSON.stringify(f)); } catch { /* sin storage: se vuelve con lo guardado, como con un F5 */ }
}
export function sacarFoto(idCampana: string): FotoAsistente | null {
  try {
    const k = claveFoto(idCampana);
    const v = sessionStorage.getItem(k);
    sessionStorage.removeItem(k);
    const f = v ? (JSON.parse(v) as Partial<FotoAsistente> | null) : null;
    if (!f || typeof f.plantillaId !== "string" || typeof f.asociada !== "string") return null;
    const p = f.prueba && typeof f.prueba === "object" && typeof f.prueba.mensajeId === "string" && typeof f.prueba.estado === "string" ? f.prueba : null;
    return { plantillaId: f.plantillaId, asociada: f.asociada, sinPrueba: f.sinPrueba === true, prueba: p, pruebaInicio: Number(f.pruebaInicio) || 0 };
  } catch {
    return null;
  }
}

/**
 * «Ahora» o «Programar» (y la fecha y hora, como las escribió) de una campaña: se eligen en «Cuándo» y se usan en
 * «Revisar y mandar», y no son parte de la campaña hasta confirmar. En sessionStorage, por campaña, así un F5 en
 * «Revisar y mandar» no la pierde (sin uno elegido, no se manda). Vale para esta pestaña, como la foto del viaje, y
 * durante una hora (`VIGENCIA_CUANDO_MS`, 29/10/2026): retomada después, vuelve sin marcar. Uno guardado sin la hora
 * en que se eligió (de antes) tampoco vale: no se sabe de cuándo es.
 */
export type CuandoElegido = { modo: "ahora" | "programar"; local: string };
export const VIGENCIA_CUANDO_MS = 60 * 60 * 1000;
const claveCuando = (idCampana: string) => `deenex-activa:oficial:cuando:${idCampana}`;
export function guardarCuando(idCampana: string, c: CuandoElegido, ahora: number = Date.now()): void {
  try { sessionStorage.setItem(claveCuando(idCampana), JSON.stringify({ ...c, en: ahora })); } catch { /* sin storage: vale mientras no se recargue */ }
}
export function leerCuando(idCampana: string, ahora: number = Date.now()): CuandoElegido | null {
  try {
    const v = sessionStorage.getItem(claveCuando(idCampana));
    const c = v ? (JSON.parse(v) as (Partial<CuandoElegido> & { en?: unknown }) | null) : null;
    if (!c || (c.modo !== "ahora" && c.modo !== "programar")) return null;
    if (typeof c.en !== "number" || !(ahora - c.en < VIGENCIA_CUANDO_MS)) {
      sessionStorage.removeItem(claveCuando(idCampana));
      return null;
    }
    return { modo: c.modo, local: typeof c.local === "string" ? c.local : "" };
  } catch {
    return null;
  }
}

/** «23/09»: el día en la zona de la marca, no en la del navegador. */
export function diaYMes(d: string | Date, zona: string = ZONA_MARCA): string {
  const local = isoALocalEnZona(d, zona);
  return `${local.slice(8, 10)}/${local.slice(5, 7)}`;
}

/**
 * El nombre con el que nace el borrador en el primer paso («Campaña del 23/09»): el día en la zona de la marca, no en la
 * del navegador (a las 23 h en Argentina, la de una máquina en UTC ya dice el día siguiente). Se cambia en «Tu campaña».
 */
export function nombrePorDefecto(d: Date, zona: string = ZONA_MARCA): string {
  return `Campaña del ${diaYMes(d, zona)}`;
}

/**
 * De qué campaña es copia un borrador («Duplicar»): lo dice la primera línea de su auditoría (`crear`, con
 * `duplicadaDe` y `nombreOrigen`). null si no es una copia. La prueba de la original no vale para la copia (vive en la
 * auditoría de la original): «Revisar y mandar» lo dice.
 */
export function origenDeLaCopia(c: Pick<CampanaDetalle, "auditoria"> | null | undefined): { id: string; nombre: string } | null {
  const crear = (Array.isArray(c?.auditoria) ? c.auditoria : []).find((a) => a?.accion === "crear");
  const d = (crear?.detalle && typeof crear.detalle === "object" ? crear.detalle : {}) as { duplicadaDe?: unknown; nombreOrigen?: unknown };
  if (typeof d.duplicadaDe !== "string" || !d.duplicadaDe) return null;
  return { id: d.duplicadaDe, nombre: typeof d.nombreOrigen === "string" && d.nombreOrigen ? d.nombreOrigen : "otra campaña" };
}

/** La línea de la prueba de una copia: empieza de cero, porque es otra campaña. */
export const textoPruebaDeLaCopia = (nombreOrigen: string) =>
  `Es una copia de «${nombreOrigen}»: la prueba empieza de cero, porque es otra campaña.`;

/**
 * Las campañas del oficial de una segmentación de Activa («Armar en el oficial», `?analisis=`), de la más nueva a la más
 * vieja (`GET /campanas?campanaActivaId=`): los borradores que se pueden seguir armando, y las que ya salieron (o están
 * programadas), para avisar antes de armar otra. Un seguimiento o una repetición no son «un borrador de esa
 * segmentación»: tienen su original. Una cancelada cuenta sólo si ya le escribió a alguien (`salio`: se cancela también a
 * mitad del envío, y lo ya enviado no se toca).
 *
 * Son todos, no la más nueva: el operador arma una campaña por grupo, cada una con su plantilla y su cupón (Joaquín,
 * 29/10/2026). Un borrador abierto no quiere decir que sea ése el que sigue, y antes de armar la tercera tiene que ver
 * las dos que ya mandó.
 */
export function delAnalisis(campanas: CampanaResumen[], analisisId: string): { borradores: CampanaResumen[]; salieron: CampanaResumen[] } {
  const suyas = (campanas || []).filter((c) => c?.audiencia && c.audiencia.tipo === "activa" && c.audiencia.campanaActivaId === analisisId);
  return {
    borradores: suyas.filter((c) => c.estado === "borrador" && !c.seguimientoDe && !c.repeticionDe),
    salieron: suyas.filter((c) => c.estado !== "borrador" && (c.estado !== "cancelada" || c.salio === true)),
  };
}

/**
 * A qué grupos de su segmentación va una campaña, en palabras: «todos los grupos» (para el backend, `grupos: []` es
 * todos), sus nombres y cuántos son («Campeones» (1 grupo)) o, si no se conocen todos los nombres (las segmentaciones
 * todavía no llegaron, o un grupo ya no está), cuántos son. Los nombres salen de las segmentaciones que carga el
 * asistente (`audiencias`).
 */
export function gruposDeLaCampana(c: Pick<CampanaResumen, "audiencia">, audiencias: AudienciaActiva[] | null | undefined): string {
  const ids = c.audiencia?.grupos ?? [];
  if (!ids.length) return "todos los grupos";
  const cuantos = `${num(ids.length)} ${plural(ids.length, "grupo", "grupos")}`;
  const grupos = (audiencias || []).find((a) => a.id === c.audiencia?.campanaActivaId)?.grupos ?? [];
  const nombres = ids.map((id) => grupos.find((g) => g.id === id)?.nombre || "");
  return nombres.every(Boolean) ? `${unirConY(nombres)} (${cuantos})` : cuantos;
}

/** Cuántas de las que ya salieron nombra el aviso de «Armar en el oficial»: las más nuevas; las demás, «y N más». */
export const SALIERON_EN_EL_AVISO = 4;

type QueSalio = Pick<CampanaResumen, "nombre" | "estado" | "programadaPara" | "confirmadaAt" | "iniciadaAt" | "createdAt" | "audiencia">;
const diaDeSalida = (c: QueSalio) => diaYMes(c.iniciadaAt || c.confirmadaAt || c.createdAt);

/**
 * El aviso de las que ya salieron (o están programadas) con esta segmentación, de la más nueva a la más vieja como las
 * devuelve `delAnalisis` (vacío sin ninguna), y con qué grupos: la que se arma puede ser para otros, y a ésos no les
 * llega de nuevo. Si alguna fue a todos los grupos, lo dice: a cualquiera que se elija le llega de nuevo.
 * - Con una: «Con esta segmentación ya salió «Campeones» el 27/10, a «Campeones» (1 grupo). Si elegís los mismos grupos,
 *   a esas personas les llega de nuevo.»
 * - Con varias, en el orden en que salieron: «Con esta segmentación ya salieron: «Campeones» el 27/10, a «Campeones»
 *   (1 grupo); «Fieles» el 28/10, a «Fieles» (1 grupo). Si elegís los mismos grupos, …». Las programadas, «para el
 *   02/11». Con más de `SALIERON_EN_EL_AVISO`, las más nuevas y «y N más».
 */
export function avisoDelAnalisis(salieron: QueSalio[], audiencias: AudienciaActiva[] | null | undefined): string {
  if (!salieron.length) return "";
  const aTodos = salieron.some((c) => !c.audiencia?.grupos?.length);
  if (salieron.length === 1) {
    const [c] = salieron;
    const cuando = c.estado === "programada"
      ? (c.programadaPara ? `está programada «${c.nombre}» para el ${diaYMes(c.programadaPara)}` : `está programada «${c.nombre}»`)
      : `salió «${c.nombre}» el ${diaDeSalida(c)}`;
    return aTodos
      ? `Con esta segmentación ya ${cuando}, a todos los grupos: a los que elijas les llega de nuevo.`
      : `Con esta segmentación ya ${cuando}, a ${gruposDeLaCampana(c, audiencias)}. Si elegís los mismos grupos, a esas personas les llega de nuevo.`;
  }
  const programadas = salieron.filter((c) => c.estado === "programada").length;
  const verbo = !programadas ? "salieron" : programadas === salieron.length ? "están programadas" : "salieron o están programadas";
  const nombradas = salieron.slice(0, SALIERON_EN_EL_AVISO).reverse().map((c) => {
    const cuando = c.estado !== "programada" ? ` el ${diaDeSalida(c)}` : c.programadaPara ? ` para el ${diaYMes(c.programadaPara)}` : "";
    return `«${c.nombre}»${cuando}, a ${gruposDeLaCampana(c, audiencias)}`;
  });
  const resto = salieron.length - nombradas.length;
  const repite = aTodos ? "Como una va a todos los grupos, a los que elijas les llega de nuevo." : "Si elegís los mismos grupos, a esas personas les llega de nuevo.";
  return `Con esta segmentación ya ${verbo}: ${nombradas.join("; ")}${resto > 0 ? `; y ${num(resto)} más` : ""}. ${repite}`;
}

/** Cuántas campañas ofrece «Empezar de una anterior». */
export const ANTERIORES_PARA_EMPEZAR = 8;

/**
 * «Empezar de una anterior» (Nueva campaña, 29/10/2026): las últimas campañas del oficial que se pueden duplicar, de la
 * más nueva a la más vieja (como llegan de `GET /campanas`). Sin borradores (ésos se retoman: no hace falta copiarlos)
 * ni seguimientos (su audiencia es la de la original, y duplicar los rechaza).
 */
export function anterioresParaEmpezar(campanas: CampanaResumen[], n: number = ANTERIORES_PARA_EMPEZAR): CampanaResumen[] {
  return (campanas || []).filter((c) => c && c.estado !== "borrador" && !c.seguimientoDe).slice(0, n);
}

/** «Salió el 27 oct 2026 · Octubre»: cuándo y a quién, de una campaña anterior (el estado va en su chip, `ESTADO_CAMPANA`). */
export function lineaDeLaAnterior(c: Pick<CampanaResumen, "estado" | "programadaPara" | "iniciadaAt" | "createdAt" | "audiencia">): string {
  const cuando = c.estado === "programada" && c.programadaPara ? `Sale el ${fecha(c.programadaPara)}`
    : c.iniciadaAt ? `Salió el ${fecha(c.iniciadaAt)}`
    : `Creada el ${fecha(c.createdAt)}`;
  return `${cuando} · ${c.audiencia?.nombre || "sin audiencia"}`;
}
